// EPOCH 21 PHASE 3: Validation Feedback System
// System for CSRs to provide feedback on parsing quality and suggest improvements
// Date: August 1, 2025

import React, { useState, useCallback } from 'react';
import { useAuthSession } from '../../hooks/useAuthSession';
import { ConversationMessage } from '../../types/lead-responses';

interface ValidationFeedback {
  conversation_hash: string;
  feedback_type: 'correction' | 'suggestion' | 'issue_report' | 'quality_rating';
  message_id?: string;
  original_value?: string;
  corrected_value?: string;
  feedback_details: string;
  quality_rating?: number; // 1-5 scale
  csr_comments: string;
  severity: 'low' | 'medium' | 'high';
  created_by: string;
  created_at: string;
}

interface ValidationFeedbackSystemProps {
  conversationHash: string;
  messages: ConversationMessage[];
  onFeedbackSubmitted?: (feedback: ValidationFeedback) => void;
  showInline?: boolean;
}

const ValidationFeedbackSystem: React.FC<ValidationFeedbackSystemProps> = ({
  conversationHash,
  messages,
  onFeedbackSubmitted,
  showInline = false
}) => {
  const { session } = useAuthSession();
  const user = session?.user;

  const [feedbackType, setFeedbackType] = useState<ValidationFeedback['feedback_type']>('correction');
  const [selectedMessageId, setSelectedMessageId] = useState<string>('');
  const [originalValue, setOriginalValue] = useState<string>('');
  const [correctedValue, setCorrectedValue] = useState<string>('');
  const [feedbackDetails, setFeedbackDetails] = useState<string>('');
  const [qualityRating, setQualityRating] = useState<number>(5);
  const [csrComments, setCsrComments] = useState<string>('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  // Handle feedback submission
  const handleSubmitFeedback = useCallback(async () => {
    if (!user || !feedbackDetails.trim()) return;

    setIsSubmitting(true);
    try {
      const feedback: ValidationFeedback = {
        conversation_hash: conversationHash,
        feedback_type: feedbackType,
        message_id: selectedMessageId || undefined,
        original_value: originalValue || undefined,
        corrected_value: correctedValue || undefined,
        feedback_details: feedbackDetails.trim(),
        quality_rating: feedbackType === 'quality_rating' ? qualityRating : undefined,
        csr_comments: csrComments.trim(),
        severity,
        created_by: user.email || user.id,
        created_at: new Date().toISOString()
      };

      // Store feedback in localStorage for now (could be extended to backend storage)
      const existingFeedback = JSON.parse(localStorage.getItem('csr_parsing_feedback') || '[]');
      const updatedFeedback = [...existingFeedback, feedback];
      localStorage.setItem('csr_parsing_feedback', JSON.stringify(updatedFeedback));

      console.log('✅ EPOCH 21 PHASE 3: Validation feedback submitted:', feedback);

      // Reset form
      setFeedbackDetails('');
      setCorrectedValue('');
      setOriginalValue('');
      setCsrComments('');
      setSelectedMessageId('');
      setShowFeedbackForm(false);

      // Notify parent component
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(feedback);
      }

      // Show success message
      alert('Feedback submitted successfully! Thank you for helping improve the parsing system.');

    } catch (error) {
      console.error('❌ Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    user, conversationHash, feedbackType, selectedMessageId, originalValue, 
    correctedValue, feedbackDetails, qualityRating, csrComments, severity, onFeedbackSubmitted
  ]);

  // Handle message selection for correction feedback
  const handleMessageSelect = useCallback((messageId: string) => {
    setSelectedMessageId(messageId);
    const message = messages.find(m => m.id === messageId);
    if (message) {
      setOriginalValue(message.sender_name || '');
      setCorrectedValue(message.sender_name || '');
    }
  }, [messages]);

  // Get severity color
  const getSeverityColor = (sev: string): string => {
    switch (sev) {
      case 'high': return 'border-red-300 bg-red-50';
      case 'medium': return 'border-yellow-300 bg-yellow-50';
      case 'low': return 'border-blue-300 bg-blue-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const feedbackForm = (
    <div className={`border rounded-lg p-4 space-y-4 ${getSeverityColor(severity)}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900">Parsing Feedback</h4>
        {!showInline && (
          <button
            onClick={() => setShowFeedbackForm(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Feedback Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Feedback Type
        </label>
        <select
          value={feedbackType}
          onChange={(e) => setFeedbackType(e.target.value as ValidationFeedback['feedback_type'])}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="correction">Parsing Correction</option>
          <option value="suggestion">Improvement Suggestion</option>
          <option value="issue_report">Issue Report</option>
          <option value="quality_rating">Quality Rating</option>
        </select>
      </div>

      {/* Message Selection for Corrections */}
      {feedbackType === 'correction' && messages.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Message to Correct
          </label>
          <select
            value={selectedMessageId}
            onChange={(e) => handleMessageSelect(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a message...</option>
            {messages.map((message, idx) => (
              <option key={message.id} value={message.id}>
                Message {idx + 1}: {message.sender_name || 'Unknown'} - {message.content.substring(0, 50)}...
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Correction Fields */}
      {feedbackType === 'correction' && selectedMessageId && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Original Value
            </label>
            <input
              type="text"
              value={originalValue}
              onChange={(e) => setOriginalValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Current parsing result"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Corrected Value
            </label>
            <input
              type="text"
              value={correctedValue}
              onChange={(e) => setCorrectedValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="What it should be"
            />
          </div>
        </div>
      )}

      {/* Quality Rating */}
      {feedbackType === 'quality_rating' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Overall Parsing Quality (1-5 stars)
          </label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setQualityRating(star)}
                className={`w-8 h-8 ${
                  star <= qualityRating ? 'text-yellow-400' : 'text-gray-300'
                } hover:text-yellow-400`}
              >
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-600">{qualityRating}/5</span>
          </div>
        </div>
      )}

      {/* Feedback Details */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {feedbackType === 'correction' ? 'Correction Details' :
           feedbackType === 'suggestion' ? 'Suggestion Details' :
           feedbackType === 'issue_report' ? 'Issue Description' :
           'Rating Comments'} *
        </label>
        <textarea
          value={feedbackDetails}
          onChange={(e) => setFeedbackDetails(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe the issue or provide details..."
          required
        />
      </div>

      {/* CSR Comments */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Comments
        </label>
        <textarea
          value={csrComments}
          onChange={(e) => setCsrComments(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Any additional context or suggestions..."
        />
      </div>

      {/* Severity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Issue Severity
        </label>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as 'low' | 'medium' | 'high')}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="low">Low - Minor improvement</option>
          <option value="medium">Medium - Noticeable issue</option>
          <option value="high">High - Significant problem</option>
        </select>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-3">
        {!showInline && (
          <button
            type="button" 
            onClick={() => setShowFeedbackForm(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmitFeedback}
          disabled={isSubmitting || !feedbackDetails.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );

  if (showInline) {
    return feedbackForm;
  }

  return (
    <div className="mt-4">
      {!showFeedbackForm ? (
        <button
          onClick={() => setShowFeedbackForm(true)}
          className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10m0 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m0 0v10a2 2 0 002 2h6a2 2 0 002-2V8M9 12h6m-6 4h6" />
          </svg>
          <span>Provide Parsing Feedback</span>
        </button>
      ) : (
        feedbackForm
      )}
    </div>
  );
};

export default ValidationFeedbackSystem;